import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { getColor } from 'helpers/utils';
import useToken from 'hooks/useToken';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  LegendComponent
]);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AA336A', '#6A33AA'];

const SellerComparisonChart = ({ height = 320, colors = COLORS }) => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [tipoVenta, setTipoVenta] = useState('nueva');
    const [data, setData] = useState([]);
    const [sellers, setSellers] = useState([]);
    const { token, setToken, idCatTipoUsuario } = useToken();
    const abortRef = useRef(null);

    const months = useMemo(() => ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'], []);
    const currentYear = new Date().getFullYear();

    const years = useMemo(() => {
        const yearList = [];
        for (let year = currentYear; year >= 2020; year--) {
            yearList.push(year);
        }
        return yearList;
    }, [currentYear]);

    const isAdmin = useMemo(() => {
        return idCatTipoUsuario === 1 || idCatTipoUsuario === '1';
    }, [idCatTipoUsuario]);

    useEffect(() => {
        if (!token) return;

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const formData = new URLSearchParams();
        formData.append('anio', selectedYear.toString());
        formData.append('tipoVenta', tipoVenta);

        fetch('/api/marketing/ObtenerComparativaVendedores', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Authorization': token ? 'Bearer ' + token : ''
            },
            body: formData.toString(),
            signal: controller.signal
        })
            .then((res) => {
                if (!res.ok) {
                    if (res.status === 401) {
                        setToken(null);
                        location.href = '';
                    }
                    throw new Error('Error fetching seller comparison');
                }
                return res.json();
            })
            .then((response) => {
                if (response.iResponseType === 1 && response.detalles) {
                    const dataset = JSON.parse(response.detalles);
                    const rows = dataset.table2 || [];

                    const sellersSet = new Set();
                    const monthlyData = {};

                    rows.forEach(item => {
                        const monthIndex = item.mes - 1;
                        const sellerName = item.vNombre || 'Sin nombre';
                        const amount = item.dTotalMXN || 0;

                        sellersSet.add(sellerName);

                        if (!monthlyData[monthIndex]) {
                            monthlyData[monthIndex] = {};
                        }
                        
                        if (!monthlyData[monthIndex][sellerName]) {
                            monthlyData[monthIndex][sellerName] = 0;
                        }
                        monthlyData[monthIndex][sellerName] += amount;
                    });

                    const sellersArray = Array.from(sellersSet);
                    setSellers(sellersArray);

                    const chartData = months.map((month, idx) => {
                        const obj = { name: month };
                        sellersArray.forEach(seller => {
                            obj[seller] = monthlyData[idx] && monthlyData[idx][seller] 
                                ? monthlyData[idx][seller] 
                                : 0;
                        });
                        return obj;
                    });

                    setData(chartData);
                } else {
                    setData([]);
                    setSellers([]);
                }
            })
            .catch((err) => {
                if (err.name === 'AbortError') return;
                console.error('Error fetching seller comparison:', err);
                setData([]);
                setSellers([]);
            });

        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, [selectedYear, tipoVenta, token]);

    const chartOptions = useMemo(() => ({
        animation: false,
        tooltip: {
            trigger: 'axis',
            padding: [7, 10],
            backgroundColor: getColor('gray-100'),
            borderColor: getColor('gray-300'),
            textStyle: { color: getColor('dark') },
            borderWidth: 1,
            confine: true,
            axisPointer: {
                type: 'shadow'
            },
            formatter: function (params) {
                const formatCurrency = (value) => {
                    return new Intl.NumberFormat('es-MX', {
                        style: 'currency',
                        currency: 'MXN',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                    }).format(value);
                };

                let result = `📅 ${params[0].axisValue}<br/>`;
                params.forEach(param => {
                    if (param.value > 0) {
                        const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>`;
                        result += `${marker}${param.seriesName}: <b>${formatCurrency(param.value)}</b><br/>`;
                    }
                });
                return result;
            }
        },
        legend: {
            data: sellers,
            textStyle: { color: getColor('gray-700') },
            type: 'scroll'
        },
        grid: { 
            top: '20%', 
            right: '5%', 
            bottom: '10%', 
            left: '10%' 
        },
        xAxis: {
            type: 'category',
            data: data.map(d => d.name),
            axisLine: {
                lineStyle: {
                    color: getColor('gray-300')
                }
            },
            axisLabel: {
                color: getColor('gray-600')
            }
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                color: getColor('gray-600'),
                formatter: function (value) {
                    return new Intl.NumberFormat('es-MX', {
                        style: 'currency',
                        currency: 'MXN',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                        notation: 'compact',
                        compactDisplay: 'short'
                    }).format(value);
                }
            },
            splitLine: {
                lineStyle: {
                    color: getColor('gray-200'),
                    type: 'dashed'
                }
            }
        },
        series: sellers.map((seller, idx) => ({
            name: seller,
            type: 'bar',
            stack: 'total',
            data: data.map(d => d[seller]),
            itemStyle: {
                color: colors[idx % colors.length]
            }
        }))
    }), [data, sellers, colors]);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: '8px' }}>
                <Form.Select 
                    size="sm" 
                    value={selectedYear} 
                    style={{ width: 120 }} 
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </Form.Select>
                <Form.Select 
                    size="sm" 
                    value={tipoVenta} 
                    style={{ width: 160 }} 
                    onChange={(e) => setTipoVenta(e.target.value)}
                    disabled={!isAdmin}
                >
                    <option value="nueva">Venta Nueva</option>
                    <option value="distribucion">Distribución</option>
                    <option value="recompra">Recompra</option>
                    <option value="todas">Todas</option>
                </Form.Select>
            </div>
            <ReactEChartsCore 
                echarts={echarts} 
                option={chartOptions} 
                style={{ height: `${height}px` }}
                opts={{ renderer: 'canvas' }}
            />
        </div>
    );
};

SellerComparisonChart.propTypes = {
    height: PropTypes.number,
    colors: PropTypes.arrayOf(PropTypes.string)
};

SellerComparisonChart.defaultProps = {
    height: 320,
    colors: COLORS
};

export default SellerComparisonChart;
